<script>
    // Обёртка одного блока худа: ставит содержимое по раскладке (проценты экрана
    // + якорь), а в режиме редактирования делает блок «хватаемым» мышью и
    // масштабируемым (колесо мыши или уголок в правом нижнем углу блока).
    import { hudLayout, hudEditMode, hudEditSelected, partStyle, applyPatch, clampScale, scaleBy, mapRect, hudGuides } from 'store/hudlayout'

    export let id;
    export let label = '';

    $: part = $hudLayout[id];
    $: selected = $hudEditSelected === id;
    $: style = partStyle(part, $mapRect);

    let el;
    let drag = null;
    let sizing = null;

    // ── направляющие: линии от блока, совпадения с соседями и расстояния
    // Прямоугольники остальных блоков берём прямо из DOM: их немного, а держать
    // отдельный реестр пришлось бы синхронизировать на каждый кадр.
    const SNAP_PX = 4;   // на сколько пикселей считаем «совпало»

    const otherRects = () => {
        const out = [];
        document.querySelectorAll('.hudbox[data-hud-id]').forEach((n) => {
            const oid = n.getAttribute('data-hud-id');
            if (oid === id) return;
            const r = n.getBoundingClientRect();
            if (r.width < 3 || r.height < 3) return;
            out.push({ l: r.left, t: r.top, r: r.right, b: r.bottom, cx: r.left + r.width / 2, cy: r.top + r.height / 2 });
        });
        return out;
    };

    const updateGuides = () => {
        if (!$hudEditMode || !el) return;
        const r = el.getBoundingClientRect();
        if (r.width < 3 || r.height < 3) return;
        const sw = window.innerWidth, sh = window.innerHeight;
        const me = { l: r.left, t: r.top, r: r.right, b: r.bottom, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
        const others = otherRects();

        const snapX = [], snapY = [], gaps = [];
        for (const o of others) {
            for (const a of [me.l, me.cx, me.r]) {
                for (const b of [o.l, o.cx, o.r]) if (Math.abs(a - b) <= SNAP_PX) snapX.push(b);
            }
            for (const a of [me.t, me.cy, me.b]) {
                for (const b of [o.t, o.cy, o.b]) if (Math.abs(a - b) <= SNAP_PX) snapY.push(b);
            }
        }

        // ближайший сосед слева/справа (с перекрытием по вертикали) и сверху/снизу
        const overlapY = (o) => Math.min(me.b, o.b) - Math.max(me.t, o.t) > 0;
        const overlapX = (o) => Math.min(me.r, o.r) - Math.max(me.l, o.l) > 0;
        const near = (list, pick) => list.reduce((best, v) => (best === null || pick(v) < pick(best) ? v : best), null);

        const left = near(others.filter((o) => overlapY(o) && o.r <= me.l), (o) => me.l - o.r);
        if (left) gaps.push({ dir: 'h', at: Math.max(me.t, left.t) + (Math.min(me.b, left.b) - Math.max(me.t, left.t)) / 2, from: left.r, size: me.l - left.r });
        const right = near(others.filter((o) => overlapY(o) && o.l >= me.r), (o) => o.l - me.r);
        if (right) gaps.push({ dir: 'h', at: Math.max(me.t, right.t) + (Math.min(me.b, right.b) - Math.max(me.t, right.t)) / 2, from: me.r, size: right.l - me.r });
        const up = near(others.filter((o) => overlapX(o) && o.b <= me.t), (o) => me.t - o.b);
        if (up) gaps.push({ dir: 'v', at: Math.max(me.l, up.l) + (Math.min(me.r, up.r) - Math.max(me.l, up.l)) / 2, from: up.b, size: me.t - up.b });
        const down = near(others.filter((o) => overlapX(o) && o.t >= me.b), (o) => o.t - me.b);
        if (down) gaps.push({ dir: 'v', at: Math.max(me.l, down.l) + (Math.min(me.r, down.r) - Math.max(me.l, down.l)) / 2, from: me.b, size: down.t - me.b });

        hudGuides.set({
            id, sw, sh,
            l: me.l, t: me.t, r: me.r, b: me.b, w: r.width, h: r.height, cx: me.cx, cy: me.cy,
            selfX: [me.l, me.cx, me.r], selfY: [me.t, me.cy, me.b],
            snapX, snapY, gaps,
        });
    };

    // направляющие показываем у выбранного блока и всё время, пока его тащат
    $: if ($hudEditMode && selected) setTimeout(updateGuides, 0);
    $: if (!$hudEditMode) hudGuides.set(null);

    // Примагничивание к центру и краям экрана (как направляющие в фотошопе)
    const SNAP = 0.7; // в процентах экрана
    const SNAP_X = [0, 25, 33.333, 50, 66.667, 75, 100];
    const SNAP_Y = [0, 25, 33.333, 50, 66.667, 75, 100];
    const snap = (v, list) => {
        for (const t of list) if (Math.abs(v - t) <= SNAP) return t;
        return v;
    };

    const onDown = (e) => {
        if (!$hudEditMode) return;
        e.preventDefault();
        e.stopPropagation();
        hudEditSelected.set(id);
        const r = $mapRect;
        const pinned = part.pin === 'map' && r && r.ok && r.w > 0 && r.h > 0;
        drag = {
            sx: e.clientX, sy: e.clientY, bx: part.x, by: part.y,
            // у привязанного к карте блока проценты считаются от размера карты
            uw: pinned ? r.w : window.innerWidth,
            uh: pinned ? r.h : window.innerHeight,
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const onMove = (e) => {
        if (!drag) return;
        const dx = (e.clientX - drag.sx) / (drag.uw || 1) * 100;
        const dy = (e.clientY - drag.sy) / (drag.uh || 1) * 100;
        applyPatch(id, {
            x: Math.round(snap(drag.bx + dx, SNAP_X) * 10) / 10,
            y: Math.round(snap(drag.by + dy, SNAP_Y) * 10) / 10,
        });
        setTimeout(updateGuides, 0);   // линии и цифры идут за блоком
    };

    const onUp = () => {
        drag = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
    };

    // ── размер блока: колесо мыши прямо над ним
    const onWheel = (e) => {
        if (!$hudEditMode) return;
        e.preventDefault();
        e.stopPropagation();
        hudEditSelected.set(id);
        scaleBy(id, e.deltaY < 0 ? 0.05 : -0.05);
    };

    // ── размер блока: уголок в правом нижнем углу (тянуть от себя = больше)
    const onSizeDown = (e) => {
        if (!$hudEditMode) return;
        e.preventDefault();
        e.stopPropagation();
        hudEditSelected.set(id);
        sizing = { sx: e.clientX, sy: e.clientY, bs: part.s || 1 };
        window.addEventListener('mousemove', onSizeMove);
        window.addEventListener('mouseup', onSizeUp);
    };

    const onSizeMove = (e) => {
        if (!sizing) return;
        const d = ((e.clientX - sizing.sx) + (e.clientY - sizing.sy)) / 260;
        applyPatch(id, { s: clampScale(sizing.bs + d) });
        setTimeout(updateGuides, 0);
    };

    const onSizeUp = () => {
        sizing = null;
        window.removeEventListener('mousemove', onSizeMove);
        window.removeEventListener('mouseup', onSizeUp);
    };
</script>

{#if part && (!part.hidden || $hudEditMode)}
    <div
        bind:this={el}
        class="hudbox"
        data-hud-id={id}
        class:edit={$hudEditMode}
        class:sel={$hudEditMode && selected}
        class:ghost={part.hidden}
        style={style}
        on:mousedown={onDown}
        on:wheel={onWheel}
        role="presentation"
    >
        {#if $hudEditMode}
            <span class="hudbox-tag">{label || id} · {Math.round((part.s || 1) * 100)}%{part.pin === 'map' ? ' · за картой' : ''}</span>
            <!-- уголок изменения размера: тянуть вправо-вниз = крупнее -->
            <span class="hudbox-size" role="presentation" title="Потяните — размер блока" on:mousedown={onSizeDown}></span>
        {/if}
        <slot />
    </div>
{/if}

<style>
    .hudbox {
        position: fixed;
        z-index: 40;
        pointer-events: none;
        /* ширина ровно по содержимому: иначе блок у правого края (панель машины)
           ужимается до остатка места от left до края экрана и схлопывается в полоску */
        width: max-content;
        max-width: none;
    }
    .hudbox.edit {
        pointer-events: auto;
        cursor: move;
        outline: 1px dashed rgba(231, 174, 63, 0.55);
        outline-offset: 3px;
    }
    .hudbox.edit.sel {
        outline: 2px solid #e7ae3f;
        box-shadow: 0 0 0 4px rgba(231, 174, 63, 0.15);
    }
    .hudbox.ghost { opacity: 0.35; }
    .hudbox-tag {
        position: absolute;
        left: 0;
        top: -1.7vh;
        white-space: nowrap;
        font: 700 0.95vh 'Manrope', sans-serif;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #e7ae3f;
        text-shadow: 0 1px 3px #000;
        pointer-events: none;
    }
    .hudbox-size {
        position: absolute;
        right: -0.75vh;
        bottom: -0.75vh;
        width: 1.5vh;
        height: 1.5vh;
        border-radius: 0.35vh;
        background: linear-gradient(135deg, #f0c863, #c8852f);
        border: 1px solid rgba(0, 0, 0, 0.55);
        box-shadow: 0 0.2vh 0.6vh rgba(0, 0, 0, 0.5);
        cursor: nwse-resize;
        pointer-events: auto;
        z-index: 2;
    }
</style>
